import { useEffect, useMemo, useState } from "react"
import { clone } from "lib/clone"
import { getDifferences, processDifferences } from "routes/schedule/compare/processDifferences"
import { BackdropClick, ComparisonUpdated, EditDialogClosed } from "event-definitions"
import noop from "lib/noop"
import { CompareTitle } from "routes/schedule/compare/compareTitle"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import { setFromValueParam } from "lib/setFromEvent"
import { Box, Stack, Tab } from "@mui/material"
import { BothPanel } from "routes/schedule/compare/bothPanel"
import { ComparisonType } from "routes/schedule/compare/comparisonType"
import "./intro-comparitor"
import "./guidance-comparitor"
import "./task-comparitor"
import "./task-order-comparitor"
import { Bound } from "lib/@components/binding/Bound"
import { useInterval } from "lib/@hooks/useInterval"
import { useRefresh } from "lib/@hooks/useRefresh"
import { useLegislation } from "routes/schedule/lib/use-legislation"
import { useSearchParam } from "lib/@hooks/use-search-param"
import { ComparisonViewTools } from "slot-definitions"
import { createSlot } from "lib/@components/slot/create-slot"

export const BothPanelWrapper = createSlot("BothPanelWrapper")

export function CompareTwoImpl({ leftCaption, rightCaption, editMode, destination, source, other = [] }) {
    const [tab, setTab] = useSearchParam("compareTab", "comparison")
    ;[leftCaption] = useSearchParam("left", leftCaption)
    ;[rightCaption] = useSearchParam("right", rightCaption)
    leftCaption = leftCaption === "" ? undefined : leftCaption
    rightCaption = rightCaption === "" ? undefined : rightCaption
    const legislation = useLegislation()
    const [compare, keepAlive, save, name] = destination
    const [otherVersion, , , otherVersionName] = other
    const [target, setTarget] = useState()
    const [to, , , comparison] = source
    const [taskStats] = useState({})
    const [commitFunctions] = useState(() => new Set())
    const refresh = useRefresh()
    if (target && otherVersion) {
        // Record which schedule and version a derived-schedule review compared against,
        // so two-way compares can't mark an out-of-date action as reviewed.
        target.saveByComparingId = otherVersion._copSourceScheduleId ?? otherVersion._id
        target.saveByComparingVersion = otherVersion.version
    }
    useInterval(keepAlive, 15000)
    useEffect(() => {
        if (!compare) return
        setTarget(clone(compare))
    }, [compare])
    const list = useMemo(buildDifferences, [target, to, otherVersion])

    taskStats.lastTask = null
    const comparisonTarget = otherVersion ?? to
    BackdropClick.useRefresh()
    ComparisonUpdated.useRefresh()
    EditDialogClosed.useRefresh()

    return (
        legislation &&
        target && (
            <Bound
                inComparison={true}
                save={save}
                leftName={name}
                rightName={otherVersionName}
                compareName={comparison}
                comparisonTarget={comparisonTarget}
                canSave={save !== noop}
                comparisons={list}
                taskStats={taskStats}
                legislation={legislation}
                timelineMode={!other}
                editMode={editMode ?? save !== noop}
                commitFunctions={commitFunctions}
                schedule={target}
                target={target}
                refresh={refresh}
                masterRefresh={refresh}
                compare={compare}
                to={to}
            >
                <CompareTitle
                    leftCaption={leftCaption}
                    rightCaption={rightCaption}
                    name={name}
                    otherVersionName={otherVersionName ?? comparison}
                />
                <TabContext value={tab}>
                    <Stack direction="row">
                        <TabList sx={{ mb: 2 }} onChange={setFromValueParam(setTab)}>
                            <Tab data-cy="differences-compare-tab" value="comparison" label="Differences" />
                            <Tab data-cy="side-by-side-compare-tab" value="both" label="Side by Side" />
                        </TabList>
                        <Box flex={1} />
                        <ComparisonViewTools.Slot />
                    </Stack>
                    <TabPanel value="both">
                        {tab === "both" && (
                            <BothPanelWrapper.Wrapper>
                                <BothPanel />
                            </BothPanelWrapper.Wrapper>
                        )}
                    </TabPanel>
                    <TabPanel value="comparison">
                        {tab === "comparison" && <ComparisonType type="List" list={list} />}
                    </TabPanel>
                </TabContext>
            </Bound>
        )
    )

    function buildDifferences() {
        if (!target) return []
        const differences = getDifferences(target, otherVersion ?? to, target)
        if (!otherVersion) return differences
        return processDifferences(
            getDifferences(target, to, target),
            getDifferences(otherVersion, to, otherVersion),
            differences
        )
    }
}
